import React, { useState } from 'react';
import { Box, Typography, Button, Stack, Alert } from '@mui/material';
import { CloudUpload, Description, Add } from '@mui/icons-material';
import { DocumentsGrid } from '../grids/DocumentsGrid';
import { UploadDocumentDialog } from '../dialogs/UploadDocumentDialog';
import { ViewDocumentDialog } from '../dialogs/ViewDocumentDialog';
import { RefundView } from '../../../../services/RefundService/types/refundTypes';
import { DocumentView } from '../../../../services/RefundService/types/documentTypes';
import { useListDocumentsQuery } from '../../../../services/RefundService';
import SectionPaper from '../../../common/components/styled-ui-elements/SectionPaper';

interface DocumentsSectionProps {
  refund: RefundView;
}

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({ refund }) => {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentView | null>(null);

  // Query for documents with real-time updates
  const { 
    data: documents = [], 
    isLoading, 
    error,
    refetch: refetchDocuments 
  } = useListDocumentsQuery({
    company: refund.company,
    center: refund.center,
    refundId: refund.id
  });

  const handleUploadClick = () => {
    setIsUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setIsUploadDialogOpen(false);
    // Refetch documents after upload
    refetchDocuments();
  };

  const handleDocumentView = (document: DocumentView) => {
    setSelectedDocument(document);
    setIsViewDialogOpen(true);
  };

  const handleViewDialogClose = () => {
    setIsViewDialogOpen(false);
    setSelectedDocument(null);
  };

  const handleDocumentDeleted = (documentId: string) => {
    // Refetch documents after deletion
    refetchDocuments();
  };

  return (
    <>
      {/* Header Section */}
      <SectionPaper sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Description sx={{ color: '#3b82f6', fontSize: 24 }} />
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1e293b',
                  mb: 0.5
                }}
              >
                Documents
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: '#64748b' }}
              >
                {documents.length === 0 
                  ? 'No documents have been uploaded for this refund yet' 
                  : `${documents.length} document${documents.length !== 1 ? 's' : ''} uploaded`
                }
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={handleUploadClick}
            sx={{
              backgroundColor: '#162346',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: '#1a2a52'
              }
            }}
          >
            Upload Documents
          </Button>
        </Stack>
      </SectionPaper>

      {/* Documents Grid Section */}
      <SectionPaper 
        sx={{ 
          border: '1px solid #e2e8f0',
          borderRadius: 1.5,
          overflow: 'hidden',
          p: 0
        }}
      >
        {documents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert 
              severity="info" 
              sx={{ 
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                '& .MuiAlert-icon': {
                  color: '#0284c7'
                }
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                No documents available
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                Click "Upload Documents" to add the first document for this refund
              </Typography>
            </Alert>
          </Box>
        ) : (
          <DocumentsGrid
            refund={refund}
            documents={documents}
            isLoading={isLoading}
            error={error}
            onDocumentView={handleDocumentView}
            onRefresh={refetchDocuments}
          />
        )}
      </SectionPaper>

      {/* Upload Dialog */}
      <UploadDocumentDialog
        open={isUploadDialogOpen}
        onClose={handleUploadDialogClose}
        refund={refund}
      />

      {/* View Dialog */}
      <ViewDocumentDialog
        open={isViewDialogOpen}
        onClose={handleViewDialogClose}
        document={selectedDocument}
        refund={refund}
        onDocumentDeleted={handleDocumentDeleted}
      />
    </>
  );
};

export default DocumentsSection;
